import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React, { useMemo } from 'react';
import { ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { HowToPlay, HowToPlayButton, useHowToPlay } from '@/components/games/HowToPlay';
import { PlayerAvatar } from '@/components/match/PlayerRatings';
import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { ShareButton } from '@/components/ui/ShareButton';
import { Skeleton } from '@/components/ui/Skeleton';
import { useThemePalette } from '@/config/theme';
import { useTotrRound } from '@/features/games/teamOfRound';
import type { TotrPlayer, TotrPredictionPlayer } from '@/lib/data/provider';
import { formatKickoffTime, formatMatchDate, formatNumber, type NumeralOptions } from '@/lib/i18n/format';
import { localizeRound } from '@/lib/i18n/footballNames';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import {
  PITCH_VERTICAL_PADDING,
  PLAYER_TOKEN_WIDTH,
  ROW_GAP,
  ROW_MIN_HEIGHT,
} from '@/lib/pitch/formation';

type Pos = 'GK' | 'DEF' | 'MID' | 'FWD';
const ROWS: Pos[] = ['FWD', 'MID', 'DEF', 'GK'];

function deadlineLabel(iso: string, options: NumeralOptions): string {
  const d = new Date(iso);
  // Date + 12-hour time (AM/PM · صباحًا/مساءً), consistent with the rest of the app.
  return `${formatMatchDate(d, options)} · ${formatKickoffTime(d, options)}`;
}

function Pip({
  photoUrl,
  name,
  teamLogo,
  teamName,
  points,
  isCaptain,
  hit,
}: {
  photoUrl: string | null;
  name: string;
  teamLogo: string | null;
  teamName: string | null;
  points?: number;
  isCaptain: boolean;
  hit: boolean;
}) {
  const palette = useThemePalette();
  const { numeralOptions } = useLocale();
  return (
    <View className="items-center gap-1" style={{ width: PLAYER_TOKEN_WIDTH }}>
      <View>
        <View
          className="rounded-full"
          style={hit ? { borderWidth: 2, borderColor: palette.win, padding: 1 } : { padding: 3 }}
        >
          <PlayerAvatar uri={photoUrl} name={name} size={44} />
        </View>
        {isCaptain ? (
          <View
            className="absolute -right-1 -top-1 h-5 w-5 items-center justify-center rounded-full border"
            style={{ backgroundColor: palette.captain, borderColor: palette.deep }}
          >
            <AppText weight="bold" style={{ color: '#1A1408', fontSize: 10 }}>
              C
            </AppText>
          </View>
        ) : null}
        {hit ? (
          <View
            className="absolute -left-1 -top-1 h-4 w-4 items-center justify-center rounded-full"
            style={{ backgroundColor: palette.win }}
          >
            <Ionicons name="checkmark" size={11} color="#06210F" />
          </View>
        ) : null}
      </View>
      {points != null ? (
        <View className="rounded-full bg-volt-glass-strong px-2 py-0.5">
          <AppText variant="micro" weight="bold" tabular className="text-volt-bright">
            {formatNumber(points, numeralOptions)}
          </AppText>
        </View>
      ) : null}
      <View className="flex-row items-center gap-1">
        {teamLogo ? <TeamCrest uri={teamLogo} name={teamName ?? ''} size="xs" /> : null}
        <AppText variant="micro" weight="medium" className="text-ink" numberOfLines={1} style={{ maxWidth: 56 }}>
          {name}
        </AppText>
      </View>
    </View>
  );
}

function Pitch({ children }: { children: React.ReactNode }) {
  return (
    <View className="mx-4 overflow-hidden rounded-card border border-pitch-line bg-pitch px-2">
      {/* Anchored markings — halfway line, centre circle and centre spot — drawn
          consistently with the match-lineup and fantasy pitches. */}
      <View className="absolute inset-x-3 top-1/2 h-px bg-pitch-line" />
      <View className="absolute left-1/2 top-1/2 h-16 w-16 -translate-x-8 -translate-y-8 rounded-full border border-pitch-line" />
      <View className="absolute left-1/2 top-1/2 h-1.5 w-1.5 -translate-x-[3px] -translate-y-[3px] rounded-full bg-pitch-line" />
      <View style={{ rowGap: ROW_GAP, paddingVertical: PITCH_VERTICAL_PADDING }}>{children}</View>
    </View>
  );
}

function shortPred(p: TotrPredictionPlayer): string {
  return p.known_as ?? p.name.split(' ').slice(-1)[0];
}
function shortActual(p: TotrPlayer): string {
  return p.known_as ?? p.name.split(' ').slice(-1)[0];
}

export default function TeamOfRoundScreen() {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { numeralOptions, locale } = useLocale();
  const howto = useHowToPlay('totr');
  const query = useTotrRound();
  const data = query.data;

  // SHOWCASE: the real best XI of the latest scored round (live or finished).
  const showcaseXi = data?.actual_xi ?? null;
  const showcaseExists = (showcaseXi?.players?.length ?? 0) > 0;
  const showcaseLive = data?.showcase_status === 'live';
  const showcasePred = useMemo(() => data?.showcase_prediction ?? [], [data?.showcase_prediction]);
  const showcaseSettled = data?.showcase_settled ?? null;

  // PREDICT: the next upcoming round's prediction game.
  const predictPred = useMemo(() => data?.my_prediction ?? [], [data?.my_prediction]);
  const predictOpen = data ? !data.locked : false;

  const actualIds = useMemo(
    () => new Set((showcaseXi?.players ?? []).map((p) => p.player_id)),
    [showcaseXi],
  );
  const showcasePredIds = useMemo(() => new Set(showcasePred.map((p) => p.player_id)), [showcasePred]);
  const actualCaptainId = showcaseXi?.captain?.player_id ?? null;

  const actualByRow = useMemo(() => {
    const map: Record<Pos, TotrPlayer[]> = { FWD: [], MID: [], DEF: [], GK: [] };
    for (const p of showcaseXi?.players ?? []) map[p.position]?.push(p);
    return map;
  }, [showcaseXi]);
  const showcasePredByRow = useMemo(() => {
    const map: Record<Pos, TotrPredictionPlayer[]> = { FWD: [], MID: [], DEF: [], GK: [] };
    for (const p of showcasePred) map[p.position]?.push(p);
    return map;
  }, [showcasePred]);
  const predictByRow = useMemo(() => {
    const map: Record<Pos, TotrPredictionPlayer[]> = { FWD: [], MID: [], DEF: [], GK: [] };
    for (const p of predictPred) map[p.position]?.push(p);
    return map;
  }, [predictPred]);

  const shareMessage = `${t('totrPredict.share')} https://play.google.com/store/apps/details?id=com.tashkeela.app`;

  return (
    <Screen>
      <HowToPlay visible={howto.visible} onClose={howto.close} i18nKey="totr" icon="shirt" />
      <BackHeader
        title={t('totr.title')}
        right={
          <View className="flex-row items-center gap-2">
            <ShareButton message={shareMessage} accessibilityLabel={t('common.share')} />
            <HowToPlayButton onPress={howto.open} />
          </View>
        }
      />
      <ScrollView contentContainerStyle={{ paddingBottom: 40, gap: 12 }}>
        {query.isPending ? (
          <View className="gap-3 px-4 pt-2">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-80 w-full" />
          </View>
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : !showcaseExists && !data?.gameweek_id ? (
          <EmptyState icon="football-outline" title={t('totr.emptyTitle')} body={t('totr.emptyBody')} />
        ) : (
          <>
            {/* ===== SHOWCASE: the real Team of the Round ===== */}
            {showcaseExists ? (
              <>
                <Card className="mx-4 mt-1 flex-row items-center gap-3 px-4 py-3.5">
                  <View className="h-11 w-11 items-center justify-center rounded-full bg-volt-glass">
                    <Ionicons name="shirt" size={20} color={palette.voltBright} />
                  </View>
                  <View className="flex-1">
                    <AppText variant="micro" className="uppercase tracking-wide text-muted">
                      {data?.showcase_round_label ? localizeRound(data.showcase_round_label, locale) : ''}
                    </AppText>
                    <View className="flex-row items-center gap-1.5">
                      {showcaseLive ? (
                        <View className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: palette.live }} />
                      ) : null}
                      <AppText variant="caption" weight="medium" className="text-soft">
                        {showcaseLive ? t('totrPredict.liveNow') : t('totr.subtitle')}
                      </AppText>
                    </View>
                  </View>
                  <View className="items-center">
                    <AppText variant="heading" weight="bold" tabular className="text-volt-bright">
                      {formatNumber(showcaseXi?.total ?? 0, numeralOptions)}
                    </AppText>
                    <AppText variant="micro" className="uppercase text-muted">
                      {t('common.pts')}
                    </AppText>
                  </View>
                </Card>

                {/* Plain-language scoring rule so the points on the pitch make sense. */}
                <Card className="mx-4 flex-row items-start gap-3 px-4 py-3">
                  <View className="mt-0.5 h-8 w-8 items-center justify-center rounded-full bg-volt-glass">
                    <Ionicons name="information-circle" size={18} color={palette.voltBright} />
                  </View>
                  <View className="flex-1">
                    <AppText variant="caption" weight="bold" className="text-ink">
                      {t('totrPredict.scoringTitle')}
                    </AppText>
                    <AppText variant="micro" className="mt-0.5 leading-4 text-soft">
                      {t('totrPredict.scoringRule')}
                    </AppText>
                  </View>
                </Card>

                {/* Your result for this round (only once it has settled). */}
                {showcaseSettled ? (
                  <Card className="mx-4 px-4 py-3.5">
                    <AppText variant="micro" weight="bold" className="mb-3 uppercase tracking-wide text-soft">
                      {t('totrPredict.yourResult')}
                    </AppText>
                    <View className="flex-row items-center justify-around">
                      <View className="items-center">
                        <AppText variant="heading" weight="bold" tabular className="text-volt-bright">
                          {formatNumber(showcaseSettled.points, numeralOptions)}
                        </AppText>
                        <AppText variant="micro" className="uppercase text-muted">
                          {t('totrPredict.yourPoints')}
                        </AppText>
                      </View>
                      <View className="h-10 w-px bg-line" />
                      <View className="items-center">
                        <AppText variant="heading" weight="bold" tabular className="text-ink">
                          {t('totrPredict.hitsOf', { hits: formatNumber(showcaseSettled.hits, numeralOptions) })}
                        </AppText>
                        <AppText variant="micro" className="uppercase text-muted">
                          {t('totrPredict.inXi')}
                        </AppText>
                      </View>
                      <View className="h-10 w-px bg-line" />
                      <View className="items-center">
                        <Ionicons
                          name={showcaseSettled.captain_hit ? 'star' : 'star-outline'}
                          size={22}
                          color={showcaseSettled.captain_hit ? palette.captain : palette.inactive}
                        />
                        <AppText variant="micro" className="uppercase text-muted">
                          {t('totrPredict.captain')}
                        </AppText>
                      </View>
                    </View>
                  </Card>
                ) : null}

                <Pitch>
                  {ROWS.map((pos) => (
                    <View
                      key={pos}
                      className="flex-row items-center justify-evenly"
                      style={{ minHeight: ROW_MIN_HEIGHT }}
                    >
                      {actualByRow[pos].map((p) => (
                        <Pip
                          key={p.player_id}
                          photoUrl={p.photo_url}
                          name={shortActual(p)}
                          teamLogo={p.team_logo}
                          teamName={p.team_name}
                          points={p.points}
                          isCaptain={p.player_id === actualCaptainId}
                          hit={showcasePredIds.has(p.player_id)}
                        />
                      ))}
                    </View>
                  ))}
                </Pitch>

                {/* The user's prediction for the showcase round, hits highlighted. */}
                {showcasePred.length > 0 ? (
                  <>
                    <AppText variant="caption" weight="bold" className="mx-4 mt-1 uppercase tracking-wide text-soft">
                      {t('totrPredict.yourPrediction')}
                    </AppText>
                    <Pitch>
                      {ROWS.map((pos) => (
                        <View
                          key={pos}
                          className="flex-row items-center justify-evenly"
                          style={{ minHeight: ROW_MIN_HEIGHT }}
                        >
                          {showcasePredByRow[pos].map((p) => (
                            <Pip
                              key={p.player_id}
                              photoUrl={p.photo_url}
                              name={shortPred(p)}
                              teamLogo={p.team_logo}
                              teamName={null}
                              isCaptain={p.is_captain}
                              hit={actualIds.has(p.player_id)}
                            />
                          ))}
                        </View>
                      ))}
                    </Pitch>
                  </>
                ) : null}
              </>
            ) : null}

            {/* ===== PREDICT: the next round's prediction game ===== */}
            {data?.gameweek_id ? (
              <View className="mx-4 mt-2 rounded-card border border-line bg-surface px-4 py-3.5">
                <View className="flex-row items-center gap-2">
                  <Ionicons name="create" size={16} color={palette.voltBright} />
                  <AppText variant="caption" weight="bold" className="flex-1 text-ink" numberOfLines={1}>
                    {t('totrPredict.predictHeader', {
                      round: data.round_label ? localizeRound(data.round_label, locale) : '',
                    })}
                  </AppText>
                </View>
                <AppText variant="micro" className="mt-1 text-muted">
                  {predictOpen
                    ? data.deadline_at
                      ? t('totrPredict.closesAt', { when: deadlineLabel(data.deadline_at, numeralOptions) })
                      : t('totrPredict.howto')
                    : t('totrPredict.lockedShort')}
                </AppText>
                {predictOpen ? (
                  <View className="mt-3">
                    <Button
                      label={predictPred.length > 0 ? t('totrPredict.edit') : t('totrPredict.cta')}
                      variant="primary"
                      onPress={() => router.push('/games/totr-predict' as never)}
                    />
                  </View>
                ) : null}
              </View>
            ) : null}

            {predictPred.length > 0 ? (
              <Pitch>
                {ROWS.map((pos) => (
                  <View
                    key={pos}
                    className="flex-row items-center justify-evenly"
                    style={{ minHeight: ROW_MIN_HEIGHT }}
                  >
                    {predictByRow[pos].map((p) => (
                      <Pip
                        key={p.player_id}
                        photoUrl={p.photo_url}
                        name={shortPred(p)}
                        teamLogo={p.team_logo}
                        teamName={null}
                        isCaptain={p.is_captain}
                        hit={false}
                      />
                    ))}
                  </View>
                ))}
              </Pitch>
            ) : null}

            {/* Leaderboard */}
            <View className="mx-4 mt-1">
              <Button
                label={t('totrPredict.leaderboard')}
                variant="secondary"
                onPress={() => router.push('/games/leaderboards-hub' as never)}
              />
            </View>
          </>
        )}
      </ScrollView>
    </Screen>
  );
}
